import type { Logger as DatadogLogger } from '@datadog/browser-logs';
import { datadogLogs } from '@datadog/browser-logs';

// Cache the Datadog logger instance
let datadogLogger: DatadogLogger | null = null;

// Track initialization state to prevent race conditions
// This prevents logs from being sent before Datadog is properly initialized
let isInitialized = false;

/**
 * Mark Datadog as initialized
 * This should be called after datadogLogs.init() completes
 *
 * IMPORTANT: This function must be called from DatadogInit.tsx after
 * the Datadog initialization is complete to prevent race conditions
 * where logs might be sent to an uninitialized Datadog instance.
 */
export function setDatadogInitialized(): void {
  isInitialized = true;
}

/**
 * Get or create a Datadog logger instance
 * Only creates the logger if Datadog has been initialized
 */
function getDatadogLogger(): DatadogLogger | null {
  if (typeof window === 'undefined' || !isInitialized) {
    return null;
  }

  if (!datadogLogger) {
    try {
      datadogLogger = datadogLogs.createLogger('pino-logger', {
        // Additional logger configuration if needed
        handler: 'http', // or 'console' for debugging
        context: {
          source: 'pino',
        },
      });
    } catch {
      // Silently fail if logger creation fails
      // This prevents logs from being sent if Datadog is misconfigured
      return null;
    }
  }

  return datadogLogger;
}

/**
 * Send log to Datadog based on log level
 */
export function sendToDatadog(level: string, msg: string, obj?: unknown): void {
  const logger = getDatadogLogger();
  if (!logger) {
    return;
  }

  // Prepare context object
  const context = obj || {};

  switch (level) {
    case 'error':
    case 'fatal': {
      logger.error(msg, context);
      break;
    }
    case 'warn': {
      logger.warn(msg, context);
      break;
    }
    case 'info': {
      logger.info(msg, context);
      break;
    }
    case 'debug': {
      logger.debug(msg, context);
      break;
    }
    case 'trace': {
      // Datadog doesn't have trace level, use debug
      logger.debug(msg, { ...context, level: 'trace' });
      break;
    }
    default: {
      logger.info(msg, context);
    }
  }
}

/**
 * Check if Datadog is initialized
 */
export function isDatadogInitialized(): boolean {
  return isInitialized;
}
